// src/components/Header.tsx
import React, { useEffect, useRef, useState } from "react";
import { Navbar, Container, Button, Overlay, Popover, Row, Col, Modal, Badge } from "react-bootstrap";
import { FaTh, FaEnvelope, FaEnvelopeOpen, FaSignOutAlt, FaBullhorn } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import ThemeMenu from "./ThemeMenu";
import { whatsNewItems } from "@/data/whatsNew";

type MenuItemRaw = {
  MenuTitle: string;
  MenuConfig: string; // JSON string like {"url": "/path", "target":"_self"}
  MenuIcon?: string;
  MenuType?: string;
};

const FA_PLACEHOLDER_STYLE: React.CSSProperties = { fontSize: "20px", marginBottom: "6px" };

function parseConfig(confString?: string) {
  if (!confString) return { url: "#", target: "_self" };
  try {
    return JSON.parse(confString);
  } catch {
    return { url: confString, target: "_self" };
  }
}

interface HeaderProps {
  acquireTokenForApi: () => Promise<string>;
  newCount: number;       // count of new messages
  onTogglePanel: () => void; // toggle notification panel
}

const Header: React.FC<HeaderProps> = ({ acquireTokenForApi, newCount, onTogglePanel }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showWhatsNewModal, setShowWhatsNewModal] = useState(false);
  const [menuItems, setMenuItems] = useState<MenuItemRaw[]>([]);
  const [loading, setLoading] = useState(false);
  const waffleRef = useRef<HTMLButtonElement | null>(null);
  const { instance } = useMsal();
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("sessionGuid");
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  };

  // Load dynamic menu
  useEffect(() => {
    const loadMenus = async () => {
      setLoading(true);
      try {
        const token = await acquireTokenForApi();

        const [defaultRes, userRes] = await Promise.all([
          fetch(`${import.meta.env.VITE_API_URL}/api/menu?type=DefaultWaffle`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${import.meta.env.VITE_API_URL}/api/menu?type=UserWaffle`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const defaultItems = defaultRes.ok ? await defaultRes.json() : [];
        const userItems = userRes.ok ? await userRes.json() : [];

        // Combine — keep distinct sections for now
        setMenuItems([...defaultItems, ...userItems]);
      } catch (err) {
        console.warn("Header: failed to load menu", err);
      } finally {
        setLoading(false);
      }
    };

    loadMenus();
  }, [acquireTokenForApi]);

  useEffect(() => {
    const saved = localStorage.getItem("m365-theme");
    if (saved) {
      const isDark = saved === "dark";
      
      document.documentElement.setAttribute("data-theme", isDark ? "dark" : "light");
    }
  }, []);

  const renderIcon = (menuIcon?: string) => {
    if (menuIcon && menuIcon.length > 0) {
      return <i className={menuIcon} style={FA_PLACEHOLDER_STYLE} aria-hidden />;
    }
    return (
      <div style={{ width: 28, height: 28, background: "var(--muted)", borderRadius: 6 }} />
    );
  };

  const latestItems = whatsNewItems.slice(0, 5);
  const totalItems = whatsNewItems.length;

  return (
    <Navbar expand={false} className="navbar fixed-top m365-header">
      <Container fluid>
        {/* Left side: waffle menu */}
        <div className="d-flex align-items-center">
          <Button
            variant="link"
            ref={waffleRef}
            onClick={() => setMenuOpen((v) => !v)}
            className="text-light p-1 me-2 waffle-btn"
            aria-label="Open solutions menu"
          >
            <FaTh size={20} />
          </Button>
          <div className="text-white fw-semibold">ENMAX M365 Management</div>
          <Overlay
            target={waffleRef.current}
            show={menuOpen}
            placement="bottom-start"
            rootClose
            onHide={() => setMenuOpen(false)}
          >
            <Popover id="waffle-popover" style={{ minWidth: 300 }}>
              <Popover.Body>
                <Row xs={2} md={3} className="g-2">
                  {loading && <div className="p-3">Loading…</div>}
                  {!loading && menuItems.length === 0 && <div className="p-3">No items</div>}
                  {menuItems.map((mi: MenuItemRaw, idx) => {
                    const cfg = parseConfig(mi.MenuConfig);
                    return (
                      <Col key={idx}>
                        <a
                          className="waffle-tile d-flex flex-column align-items-center text-center text-dark text-decoration-none"
                          href={cfg.url || "#"}
                          target={cfg.target || "_self"}
                          rel={cfg.target === "_blank" ? "noreferrer" : undefined}
                        >
                          {renderIcon(mi.MenuIcon)}
                          <small>{mi.MenuTitle}</small>
                        </a>
                      </Col>
                    );
                  })}
                </Row>
              </Popover.Body>
            </Popover>
          </Overlay>
        </div>

        {/* Right side: notification + theme toggle + logout */}
        <div className="d-flex align-items-center">
          <ThemeMenu />
          <Button
            variant="link"
            className="text-light me-2 whats-new-btn"
            title="What's New"
            onClick={() => setShowWhatsNewModal(true)}
          >
            <FaBullhorn />
          </Button>
          {/* Notifications */}
          <Button
            variant="link"
            className={`toggle-messages text-light me-2 ${
              newCount > 0 ? "new-message" : ""
            }`}
            title="Notifications"
            onClick={onTogglePanel}
          >
            {newCount > 0 ? <FaEnvelopeOpen /> : <FaEnvelope />}
          </Button>
          {/* Logout */}
          <Button
            variant="link"
            className="text-light p-1"
            title="Logout"
            onClick={handleLogout}
            aria-label="Logout"
          >
            <FaSignOutAlt size={20} />
          </Button>
        </div>
      </Container>

      <Modal show={showWhatsNewModal} onHide={() => setShowWhatsNewModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>What&apos;s New</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {latestItems.map((item) => (
            <div key={item.id} className="whats-new-modal-item mb-3">
              <div className="d-flex justify-content-between align-items-center gap-2 mb-1">
                <strong>{item.title}</strong>
                <Badge bg="secondary">{item.area}</Badge>
              </div>
              <div className="text-muted small mb-1">{item.date}</div>
              <div>{item.summary}</div>
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <small className="text-muted">Showing latest {latestItems.length} of {totalItems} updates</small>
          <Button
            onClick={() => {
              setShowWhatsNewModal(false);
              navigate("/whats-new");
            }}
          >
            View full changelog
          </Button>
        </Modal.Footer>
      </Modal>
    </Navbar>
  );
};

export default Header;